import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  BadgeCheck,
  CircleX,
  CreditCard,
  Eye,
  Search,
  Store,
} from 'lucide-react';
import { Pagination } from '../../components/common/Pagination';
import { Modal } from '../../components/common/Modal';
import { SegmentedToggle } from '../../components/common/SegmentedToggle';
import { usePaginationState } from '../../hooks/usePaginationState';
import { useLanguage } from '../../features/language/useLanguage';
import {
  lynkPaymentEventService,
  type LynkPaymentEvent,
  type LynkPaymentEventState,
} from '../../services/lynkPaymentEventService';

export default function LynkPaymentEventsPage() {
  const { t } = useLanguage();
  const { page, size, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  const [state, setState] = useState<LynkPaymentEventState>('all');
  const [search, setSearch] = useState('');
  const [preview, setPreview] = useState<LynkPaymentEvent | null>(null);

  resetOnChange([state, search]);

  const list = useQuery({
    queryKey: ['admin', 'lynk-payment-events', { page, size, state, search }],
    queryFn: () => lynkPaymentEventService.list({ page, size, state, search }),
  });

  const rows = useMemo(() => list.data?.data ?? [], [list.data?.data]);
  const total = list.data?.total ?? 0;

  const stats = useMemo(() => {
    let applied = 0;
    let failed = 0;
    for (const row of rows) {
      if (row.applied) applied += 1;
      else failed += 1;
    }
    return { total, applied, failed };
  }, [rows, total]);

  const stateItems: { key: LynkPaymentEventState; label: string }[] = [
    { key: 'all', label: t('lynkPayments.filters.all') },
    { key: 'applied', label: t('lynkPayments.filters.applied') },
    { key: 'failed', label: t('lynkPayments.filters.failed') },
  ];

  return (
    <div className="space-y-6" data-test-id="lynk-payment-events-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t('lynkPayments.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('lynkPayments.subtitle')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          icon={<CreditCard className="h-4 w-4" />}
          label={t('lynkPayments.stats.total')}
          value={stats.total}
          tone="primary"
        />
        <StatCard
          icon={<BadgeCheck className="h-4 w-4" />}
          label={t('lynkPayments.stats.applied')}
          value={stats.applied}
          tone="success"
        />
        <StatCard
          icon={<CircleX className="h-4 w-4" />}
          label={t('lynkPayments.stats.failed')}
          value={stats.failed}
          tone="danger"
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <SegmentedToggle
          ariaLabel={t('lynkPayments.filters.state')}
          value={state}
          onChange={setState}
          minTabWidth="100px"
          testIdPrefix="lynk-payment-filter"
          items={stateItems}
        />
        <div className="relative w-full lg:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('lynkPayments.searchPlaceholder')}
            className="w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {list.isLoading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">{t('common.loading')}</div>
        ) : rows.length === 0 ? (
          <div className="px-6 py-16 text-center text-sm text-gray-500">
            {t('lynkPayments.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <Th>{t('lynkPayments.columns.receivedAt')}</Th>
                  <Th>{t('lynkPayments.columns.customerEmail')}</Th>
                  <Th>{t('lynkPayments.columns.item')}</Th>
                  <Th>{t('lynkPayments.columns.amount')}</Th>
                  <Th>{t('lynkPayments.columns.status')}</Th>
                  <Th>{t('lynkPayments.columns.messageId')}</Th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap tabular-nums">
                      {format(new Date(row.received_at), 'PP p')}
                    </td>
                    <td className="px-4 py-3 text-gray-800">{row.customer_email}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-900 font-medium">{row.item_title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        {row.tier ? `${t('lynkPayments.tierLabel')}: ${row.tier}` : t('lynkPayments.unknownTier')}
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-gray-900 tabular-nums">{row.amount_raw}</td>
                    <td className="px-4 py-3">
                      <StatusBadge applied={row.applied} />
                    </td>
                    <td className="px-4 py-3">
                      <code className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {row.message_id}
                      </code>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setPreview(row)}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 px-3 py-1.5 text-xs font-medium transition-colors"
                        data-test-id={`lynk-event-detail-${row.id}`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        {t('common.view')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination
          page={page}
          pageSize={size}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setSize}
          isLoading={list.isLoading}
        />
      </div>

      {preview && <LynkEventDetailModal event={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  tone: 'primary' | 'success' | 'danger';
}) {
  const toneMap: Record<typeof tone, string> = {
    primary: 'bg-primary-50 text-primary-700',
    success: 'bg-emerald-50 text-emerald-700',
    danger: 'bg-rose-50 text-rose-700',
  };
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</span>
        <span className={`flex h-7 w-7 items-center justify-center rounded-md ${toneMap[tone]}`}>
          {icon}
        </span>
      </div>
      <div className="mt-2 text-2xl font-semibold text-gray-900 tabular-nums">{value}</div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500">{children}</th>
  );
}

function StatusBadge({ applied }: { applied: boolean }) {
  const { t } = useLanguage();
  if (applied) {
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200 px-2 py-0.5 text-xs font-semibold">
        <BadgeCheck className="h-3 w-3" />
        {t('lynkPayments.status.applied')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200 px-2 py-0.5 text-xs font-semibold">
      <CircleX className="h-3 w-3" />
      {t('lynkPayments.status.failed')}
    </span>
  );
}

function LynkEventDetailModal({
  event,
  onClose,
}: {
  event: LynkPaymentEvent;
  onClose: () => void;
}) {
  const { t } = useLanguage();

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('lynkPayments.detail.title')}
      maxWidth="4xl"
      headerAction={
        <span className="text-xs text-gray-500 font-medium">
          {format(new Date(event.received_at), 'PP p')}
        </span>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <InfoCard label={t('lynkPayments.columns.customerEmail')} value={event.customer_email} />
          <InfoCard label={t('lynkPayments.columns.amount')} value={event.amount_raw} />
          <InfoCard label={t('lynkPayments.columns.item')} value={event.item_title} />
          <InfoCard label={t('lynkPayments.columns.refId')} value={event.ref_id} />
          <InfoCard label={t('lynkPayments.columns.event')} value={event.event} />
          <InfoCard label={t('lynkPayments.columns.messageId')} value={event.message_id} />
          <InfoCard label={t('lynkPayments.columns.company')} value={event.company_name ?? '-'} />
          <InfoCard
            label={t('lynkPayments.columns.applyError')}
            value={event.apply_error ?? t('lynkPayments.noError')}
          />
        </div>

        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-200 bg-gray-50">
            <Store className="h-4 w-4 text-primary-700" />
            <span className="text-sm font-medium text-gray-800">{t('lynkPayments.detail.rawPayload')}</span>
          </div>
          <pre className="max-h-[420px] overflow-auto p-4 text-xs bg-white text-gray-800">
            {JSON.stringify(event.raw_payload ?? {}, null, 2)}
          </pre>
        </div>
      </div>
    </Modal>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5">
      <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="mt-1 text-sm text-gray-900 break-all">{value}</div>
    </div>
  );
}
